// מודל Screen סלחני לצד הלקוח: כל בלוק מאומת לבד מול zod של core; בלוק לא מוכר/פגום → raw,
// לא נזרק ולא מפיל את שאר המסך (09 §1, 11 §1). טהור — בלי DOM.
import { AppBlock, Block, Notice } from '@aibify/core';
import type { ChartBlock, TableBlock } from '@aibify/core';
import { isExplorer } from './kit/series.js';

export type RawBlock = { type: 'raw'; id: string; original: unknown; originalType: string; reason: string };
export type ViewBlock = Block | RawBlock;
export type ViewScreen = { blocks: ViewBlock[]; asOf: string | null; notices: Notice[] };

/**
 * בלוק app מתוך פלט של ריצה (display.preview {type:'app', source:{runId}} בלי html — באג 0d66fcc8): בלוק ה-app של הריצה
 * לפי id, ואם אין כזה — הראשון שיש לו קוד. null = אין בפלט בלוק app עם html/js.
 */
export function pickRunApp(output: unknown, blockId: string): AppBlock | null {
  const blocks = (output as { blocks?: unknown } | null)?.blocks;
  if (!Array.isArray(blocks)) return null;
  const apps = blocks.map((b) => AppBlock.safeParse(b)).flatMap((p) => (p.success && (p.data.html || p.data.js) ? [p.data] : []));
  return apps.find((b) => b.id === blockId) ?? apps[0] ?? null;
}

export function parseScreen(input: unknown): ViewScreen {
  const o = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const blocks = Array.isArray(o.blocks) ? o.blocks : [];
  const notices = Array.isArray(o.notices) ? o.notices.flatMap((n) => {
    const r = Notice.safeParse(n);
    return r.success ? [r.data] : [];
  }) : [];
  const asOf = typeof o.asOf === 'string' && o.asOf ? o.asOf : null;
  return {
    asOf,
    notices,
    blocks: blocks.map((b, i): ViewBlock => {
      const r = Block.safeParse(b);
      if (r.success) return r.data;
      const bo = (b && typeof b === 'object' ? b : {}) as Record<string, unknown>;
      return {
        type: 'raw',
        id: typeof bo.id === 'string' ? bo.id : `raw-${i}`,
        original: b,
        originalType: typeof bo.type === 'string' ? bo.type : 'unknown',
        reason: r.error.issues[0]?.message ?? 'invalid',
      };
    }),
  };
}

export type Group =
  | { kind: 'single'; block: ViewBlock }
  | { kind: 'pair'; chart: ChartBlock; table: TableBlock };

/**
 * pairBlocks (מ-v1 viz_pair.mjs): chart+table **סמוכים** → תצוגה אחת. סמיכות ולא דמיון תוכן —
 * הסדר הוא הצהרת השרת. צמד נבלע פעם אחת (chart·table·chart → צמד + בודד).
 * גרף בחוקר סדרות לא נכנס לצמד — יש לו כבר טבלה משלו (המקרא).
 */
export function pairBlocks(blocks: ViewBlock[]): Group[] {
  const out: Group[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const a = blocks[i]!;
    const b = blocks[i + 1];
    const solo = (x: ViewBlock) => x.type === 'chart' && isExplorer(x);
    if (b && (solo(a) || solo(b))) out.push({ kind: 'single', block: a });
    else if (b && a.type === 'chart' && b.type === 'table') {
      out.push({ kind: 'pair', chart: a, table: b });
      i++;
    } else if (b && a.type === 'table' && b.type === 'chart') {
      out.push({ kind: 'pair', chart: b, table: a });
      i++;
    } else out.push({ kind: 'single', block: a });
  }
  return out;
}
